<template>
	<div>
		<x-modal
			:wWidth="1080"
			v-if="isVisible"
			@cancel="sendSettlementAdviceClose"
			@close="sendSettlementAdviceClose"
			@ok="sendSettlementAdviceSubmit"
			:draggable="true"
			:resizable="true"
			:loading="loading"
			:okDisabled="sendSettlementAdviceButtonDisabled"
			okText="Send Settlement Advice"
		>
			<div slot="title" :class="`d-flex justify-content-between align-items-center`">Send Settlement Advice</div>

			<div class="container">
				<el-form label-width="140px">

                    <el-form-item v-if="!_.isEmpty(settlementAdvices) && !Ambition.useApplicationAudits()" class="mt-4" label="Settlement">
						<el-select collapse-tags multiple class="w-100" v-model="selectedAdvices" placeholder="Please select all to confirm and continue">
                            <el-option v-for="advice in settlementAdvices" :key="advice.value" :label="advice.label" :value="advice.value"></el-option>
                        </el-select>
                        <small>Please select all to confirm completion</small>
					</el-form-item>

					<div v-if="hasSettlementAdviceBeenMet">

                        <email-sending-options></email-sending-options>

						<email-recipients
							v-if="(emailBuilder.sendEmail || emailBuilder.sendSms)"
							:recipientType="recipientTypes"
							:autoSelectType="recipientTypes"
							:recipientAllowCreate="false"
                            :hideCC="!emailBuilder.sendEmail ? true : false"
                            :hideBCC="!emailBuilder.sendEmail ? true : false"
						></email-recipients>

						<email-task v-model="emailBuilder.task"></email-task>

                        <el-form-item label="Customer Review" v-if="Ambition.customerReviewRequest()">
                            <el-switch
                                v-model="sendReviewRequest"
                                active-color="#5cbb2a"
                                inactive-color="#dc3545"
                                :active-value="true"
                                active-text="Send the customer a review request when the application is completed"
                                :inactive-value="false"
                            ></el-switch>
                            <el-input class="mt-3" :rows="4" v-if="!sendReviewRequest" type="textarea" v-model="noReviewRequestReason" placeholder="Reason for not sending a review request"></el-input>
                        </el-form-item>

						<el-divider v-if="!_.isEmpty(emailBuilder.recipients) && (emailBuilder.sendEmail || emailBuilder.sendSms)"></el-divider>

						<email-markup-multi
							class="mb-3"
							v-if="!_.isEmpty(emailBuilder.recipients) && (emailBuilder.sendEmail || emailBuilder.sendSms)"
							action="send-settlement-advice"
							:preselectAttachments="settlementDocuments"
						></email-markup-multi>
					</div>
				</el-form>
			</div>
		</x-modal>
	</div>
</template>

<script>
    import EmailSendingOptions from "@ambition/components/messages/EmailSendingOptions.vue"
	import EmailRecipients from "@ambition/components/messages/EmailRecipients"
	import EmailAttachments from "@ambition/components/messages/EmailAttachments"
	import EmailMarkupMulti from "@ambition/components/messages/EmailMarkupMulti"
	import EmailTask from "@ambition/components/messages/EmailTask"
	import { mapGetters, mapState } from "vuex"

	export default {
		name: "BuilderSendLoanContractModal",
		props: {
			visible: {
				required: true,
				type: Boolean,
			},
			remittanceDate: {
				type: String,
                default: ''
			},
			lenderContractNumber: {
				type: String,
                default: ''
			},
			applicants: {
				required: true,
				type: Array,
			},
			headers: {
				required: true,
				type: Object,
			},
			settlementDocuments: {
				required: true,
				type: Array,
			},
		},
		components: {
            EmailSendingOptions,
			EmailRecipients,
			EmailAttachments,
			EmailMarkupMulti,
			EmailTask,
		},
		watch: {
			visible() {
				this.isVisible = this.visible
			},
            application: {
                handler(application) {
                    this.sendReviewRequest = application.send_review_request
                    this.noReviewRequestReason = application.no_review_request_reason
                },
                deep: true,
                immediate: true
            }
		},
		data() {
			return {
				settlementAdvices: [],
				selectedAdvices: [],
                sendReviewRequest: true,
                noReviewRequestReason: '',
				isVisible: false,
				loading: false,
				recipientTypes: ["applicant", "seller", "dealer"],
			}
		},
		mounted() {
			this.getSettlementRequirements()
		},
		activated() {},
		computed: {
			...mapGetters({
                application: 'initAppData',
				docDocuments: "documents/all",
				emailBuilder: "builderEmail/getEmail",
                templatesLoaded: "builderEmail/checkTemplatesLoaded",
			}),
			sendSettlementAdviceButtonDisabled() {
                if (Ambition.customerReviewRequest()) {
                    if (!this.sendReviewRequest && !this.noReviewRequestReason) return true
                }
				if (!this.emailBuilder.sendEmail && !this.emailBuilder.sendSms) return false
                if (!this.templatesLoaded) return true
                if (this.emailBuilder.delaySending && !this.emailBuilder.sendAt) return true
				if (_.size(this.emailBuilder.recipients) >= 1 && !this.loading && this.hasSettlementAdviceBeenMet) return false
				return true
			},
			hasSettlementAdviceBeenMet() {
                if (_.isEmpty(this.settlementAdvices) || Ambition.useApplicationAudits()) return true
				if (_.isEmpty(this.selectedAdvices)) return false
                let requiredAdvices = this.settlementAdvices.filter(settlementAdvice => settlementAdvice.required == 1)
                if (_.size(this.selectedAdvices) == _.size(requiredAdvices)) {
                    return true
                }
				return false
			},
		},
		methods: {
			getSettlementRequirements() {
				axios
					.get(`/api/applications/${this.$store.getters.initAppData.id}/send-and-settle/requirements`, this.headers)
					.then((res) => {
						let settlements = res.data
						this.settlementAdvices = []
						settlements.forEach((element) => {
							this.settlementAdvices.push({
								label: element.question,
								value: element.id,
								required: element.required,
							})
						})
					})
					.catch((err) => {
						console.error(err)
					})
			},
			sendSettlementAdviceClose() {
				this.$store.dispatch("builderEmail/resetEmail")
				this.loading = false
				this.isVisible = false
				this.$emit("update:visible", false)
			},

			sendSettlementAdviceSubmit() {
				this.loading = true

                this.$store.dispatch('application/setSendReviewRequest', this.sendReviewRequest)
                this.$store.dispatch('application/setNoReviewRequestReason', this.noReviewRequestReason)

				if (!this.$store.getters.initAppData.application_status_id || this.$store.getters.initAppData.application_status_id <= 13) {
					this.$store.dispatch("statusIndicators/changeStatus", {
						stage: "settled",
						value: 2,
						resent: false,
						token: this.$store.getters.token,
						appId: this.$store.getters.initAppData.id,
						remittanceDate: this.remittanceDate,
						lenderContractNumber: this.lenderContractNumber,
						email: this.emailBuilder,
                        sendReviewRequest: this.sendReviewRequest,
                        noReviewRequestReason: this.noReviewRequestReason,
					})
				} else if (this.$store.getters.initAppData.application_status_id > 13) {
					this.$store.dispatch("statusIndicators/changeStatus", {
						stage: "settled",
						value: 2,
						resent: true,
						token: this.$store.getters.token,
						appId: this.$store.getters.initAppData.id,
						remittanceDate: this.remittanceDate,
						lenderContractNumber: this.lenderContractNumber,
						email: this.emailBuilder,
                        sendReviewRequest: this.sendReviewRequest,
                        noReviewRequestReason: this.noReviewRequestReason,
					})
				}

				// create application task
                if (_.get(this.emailBuilder, 'task.create', false)) {
                    this.$store.dispatch("statusIndicators/changeStatus", {
                        stage: "createTask",
                        token: this.$store.getters.token,
                        remindMe: this.$moment(this.emailBuilder.task.remindMe).format("YYYY-MM-DD"),
                        taskName: this.emailBuilder.task.name,
                        time: this.emailBuilder.task.time,
                        userId: this.emailBuilder.task.userId,
                        appId: this.$store.getters.initAppData.id
                    })
                }

                    // create recipient tasks
                if (_.get(this.emailBuilder, 'recipients', false)) {
                    this.emailBuilder.recipients.forEach(recipient => {
                        if (_.get(recipient, 'template.task.create', false)) {
                            this.$store.dispatch("statusIndicators/changeStatus", {
                                stage: "createTask",
                                token: this.$store.getters.token,
                                remindMe: this.$moment(recipient.template.task.remindMe).format("YYYY-MM-DD"),
                                taskName: recipient.template.task.name,
                                time: recipient.template.task.time,
                                userId: recipient.template.task.userId,
                                appId: this.$store.getters.initAppData.id
                            })
                        }
                    })
                }

				swal({
					title: "Success",
					type: "success",
					text: "Settlement Advice Sent",
				})

				this.sendSettlementAdviceClose()
			},
		},
	}
</script>
